import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, MapPin, Euro, ArrowLeft, LogOut, User } from 'lucide-react';

// Configure moment to use French locale
moment.locale('fr');
const localizer = momentLocalizer(moment);

const PrestataireCalendar = () => {
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const res = await axios.get(`${API}/calendar/events`, { withCredentials: true });
      
      // Transform events for react-big-calendar
      const transformedEvents = res.data.map(event => ({
        id: event.id,
        title: event.title,
        start: new Date(event.start),
        end: new Date(event.end),
        resource: event // Store full event data
      }));
      
      setEvents(transformedEvents);
    } catch (error) {
      console.error('Error loading calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  // Custom event style
  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: '#d97706',
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block'
    };
    return { style };
  };

  const messages = {
    allDay: 'Journée',
    previous: 'Précédent',
    next: 'Suivant',
    today: "Aujourd'hui",
    month: 'Mois',
    week: 'Semaine',
    day: 'Jour',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Heure',
    event: 'Événement',
    noEventsInRange: 'Aucun événement dans cette période',
    showMore: total => `+ ${total} événement(s) supplémentaire(s)`
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <header className="bg-white border-b border-stone-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_wedding-erp/artifacts/6y9ywpy7_image.png" 
                alt="Kolnoa" 
                className="h-12"
              />
              <div>
                <h1 className="text-xl font-bold text-stone-800">Portail Prestataire</h1>
                <p className="text-sm text-stone-600">Mon calendrier</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-stone-600">
                <User className="h-4 w-4" />
                <span className="text-sm">{user?.name}</span>
              </div>
            </div>
          </div>
        </header>
        <div className="flex items-center justify-center h-64">
          <div className="text-stone-500">Chargement du calendrier...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_wedding-erp/artifacts/6y9ywpy7_image.png" 
              alt="Kolnoa" 
              className="h-12"
            />
            <div>
              <h1 className="text-xl font-bold text-stone-800">Portail Prestataire</h1>
              <p className="text-sm text-stone-600">Mon calendrier</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/prestataire-portal">
              <Button variant="outline" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Retour aux missions
              </Button>
            </Link>
            <div className="flex items-center gap-2 text-stone-600">
              <User className="h-4 w-4" />
              <span className="text-sm">{user?.name}</span>
            </div>
            <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-stone-800 mb-2 flex items-center gap-2">
            <CalendarIcon className="h-7 w-7 text-amber-600" />
            Mon Calendrier
          </h2>
          <p className="text-stone-600">Événements où vous êtes assigné</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-4">
                <div style={{ height: '600px' }}>
                  <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    onSelectEvent={handleSelectEvent}
                    eventPropGetter={eventStyleGetter}
                    messages={messages}
                    views={['month', 'week', 'day', 'agenda']}
                    defaultView="month"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Event Details Sidebar */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Détails de l'événement</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedEvent ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg text-stone-900 mb-2">
                        {selectedEvent.prestation}
                      </h3>
                      <Badge className="bg-amber-100 text-amber-700">
                        {selectedEvent.statut || 'Planifié'}
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <CalendarIcon className="h-4 w-4 text-stone-500 mt-1" />
                        <div>
                          <p className="text-sm font-medium text-stone-700">Date</p>
                          <p className="text-sm text-stone-600">
                            {moment(selectedEvent.start).format('dddd D MMMM YYYY')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <span className="text-lg mt-0.5">👤</span>
                        <div>
                          <p className="text-sm font-medium text-stone-700">Client</p>
                          <p className="text-sm text-stone-600">{selectedEvent.client}</p>
                        </div>
                      </div>

                      {selectedEvent.lieu && (
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-stone-500 mt-1" />
                          <div>
                            <p className="text-sm font-medium text-stone-700">Lieu</p>
                            <p className="text-sm text-stone-600">{selectedEvent.lieu}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-stone-500">
                    <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-stone-300" />
                    <p className="text-sm">Sélectionnez un événement pour voir les détails</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stats */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-lg">Mes Statistiques</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-stone-600">Mes événements</span>
                    <span className="font-semibold text-stone-900">{events.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-stone-600">Ce mois</span>
                    <span className="font-semibold text-amber-600">
                      {events.filter(e => moment(e.start).isSame(moment(), 'month')).length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-stone-600">À venir (7 jours)</span>
                    <span className="font-semibold text-green-600">
                      {events.filter(e => {
                        const eventMoment = moment(e.start);
                        return eventMoment.isAfter(moment()) && eventMoment.isBefore(moment().add(7, 'days'));
                      }).length}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrestataireCalendar;
